/**
 * Change Event Tracking Handler
 *
 * Trackt Change-Events von Formular-Elementen und sendet sie als "click" Events
 * an den DataLayer (für Abwärtskompatibilität).
 *
 * Unterstützte Elemente:
 * - <select>
 * - <input type="radio">
 * - <input type="checkbox">
 *
 * Das Event erhält einen zusätzlichen Parameter event_trigger: "change"
 * zur Kennzeichnung, dass es durch ein Change-Event ausgelöst wurde.
 *
 * @example
 * <div component="filter">
 *   <select name="sort">
 *     <option value="price_asc">Preis aufsteigend</option>
 *   </select>
 * </div>
 *
 * Output:
 * {
 *   event: "click",
 *   event_trigger: "change",
 *   component: "filter",
 *   interaction: "sort",
 *   label: "Preis aufsteigend",
 *   value: "price_asc"
 * }
 */
import DataLayerService from "../../../DataLayerService.js";
import isValidComponent from "../../common/isValidComponent.js";
import { collectComponentAttributes } from "../../common/component-attributes.js";

const datalayerService = new DataLayerService();

// Registriere Change Event Listener
document.addEventListener("change", handleChange);

/**
 * Haupt-Change-Handler
 * Erstellt click Events für select, radio und checkbox Elemente
 */
export function handleChange(event) {
	const { target } = event;

	// Prüfe ob Element in einer validen Komponente ist
	if (!isValidComponent(target)) {
		return;
	}

	// Prüfe ob es ein trackbares Element ist
	if (!isTrackableElement(target)) {
		return;
	}

	// Erstelle click Event
	trackChange(target);
}

/**
 * Prüft ob Element getrackt werden soll
 * Unterstützt: select, radio, checkbox
 */
function isTrackableElement(element) {
	if (element.tagName === "SELECT") {
		return true;
	}

	if (element.tagName === "INPUT") {
		const type = element.type.toLowerCase();
		return type === "radio" || type === "checkbox";
	}

	return false;
}

/**
 * Erstellt click Event für Change-Event
 */
function trackChange(target) {
	const params = {};

	// event_trigger zur Kennzeichnung
	params.event_trigger = "change";

	// component (vom nächsten component-Element)
	const componentElement = target.closest("[component]");
	if (componentElement) {
		params.component = componentElement.getAttribute("component");
	}

	// list (optional von product-list)
	const productListElement = target.closest("[product-list]");
	if (productListElement) {
		params.list = productListElement.getAttribute("product-list");
	}

	// interaction (name Attribut oder id)
	params.interaction = getInteraction(target);

	// label (gewählter Text)
	params.label = getLabel(target);

	// value (gewählter Wert)
	// Checkbox-Standard-Wert "on" ignorieren (wenn kein explizites value-Attribut gesetzt)
	if (target.value && !(target.type === "checkbox" && target.value === "on" && !target.hasAttribute("value"))) {
		params.value = target.value;
	}

	// Sammle zusätzliche Attribute vom component-Element
	const componentAttributes = collectComponentAttributes(target);
	Object.assign(params, componentAttributes);

	// Pushe Event als "click"
	datalayerService.pushEvent("click", params);
}

/**
 * Ermittelt interaction Wert
 * Priorität: name Attribut → id → tagName
 */
function getInteraction(element) {
	if (element.name) {
		return element.name;
	}

	if (element.id) {
		return element.id;
	}

	// Fallback: Typ des Elements
	if (element.tagName === "SELECT") {
		return "select";
	}

	return element.type || "input";
}

/**
 * Ermittelt label Wert
 * Für select: Text der gewählten Option
 * Für radio/checkbox: Zugehöriges <label> oder Wert
 */
function getLabel(element) {
	if (element.tagName === "SELECT") {
		const selectedOption = element.options[element.selectedIndex];
		return selectedOption?.text || element.value || "";
	}

	if (element.tagName === "INPUT") {
		// Suche nach zugehörigem Label
		const label = findLabelForInput(element);
		if (label) {
			return label.textContent?.trim() || "";
		}

		// Fallback: value
		return element.value || "";
	}

	return "";
}

/**
 * Findet das zugehörige Label für ein Input-Element
 */
function findLabelForInput(input) {
	// 1. Label mit for-Attribut
	if (input.id) {
		const labelWithFor = document.querySelector(`label[for="${input.id}"]`);
		if (labelWithFor) {
			return labelWithFor;
		}
	}

	// 2. Parent Label
	const parentLabel = input.closest("label");
	if (parentLabel) {
		return parentLabel;
	}

	return null;
}
